import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import Api from '../../services/api';
import { Operator } from '../../types/api.types';

interface OperatorInfoState {
  operators: Operator[];
  selectedOperator: Operator | null;
  operatorInjured: boolean | null;
  fetching: boolean;
  error: string | null;
}

const initialState: OperatorInfoState = {
  operators: [],
  selectedOperator: null,
  operatorInjured: null,
  fetching: false,
  error: null
};

// Async thunks
export const fetchOperators = createAsyncThunk(
  'operatorInfo/fetchOperators',
  async () => {
    const operators = await Api.getOperators();
    return operators;
  }
);

const operatorInfoSlice = createSlice({
  name: 'operatorInfo',
  initialState,
  reducers: {
    selectOperator: (state, action: PayloadAction<Operator>) => {
      state.selectedOperator = action.payload;
      state.error = null;
    },
    setOperatorInjured: (state, action: PayloadAction<boolean>) => {
      state.operatorInjured = action.payload;
    },
    clearOperatorInfo: (state) => {
      state.selectedOperator = null;
      state.operatorInjured = null;
      state.error = null;
    },
    setOperators: (state, action: PayloadAction<Operator[]>) => {
      state.operators = action.payload;
    },
    setCurrentUserAsOperator: (state, action: PayloadAction<{ email: string; name: string }>) => {
      const { email, name } = action.payload;
      const nameParts = name.split(' ');
      const firstName = nameParts[0] || '';
      const lastName = nameParts.slice(1).join(' ') || '';
      
      const currentUserOperator: Operator = {
        id: 'current-user',
        operatorId: 'current-user',
        userId: 'current-user',
        email,
        firstName,
        lastName,
        employeeNumber: ''
      };
      
      state.selectedOperator = currentUserOperator;
    }
  },
  extraReducers: (builder) => {
    builder.addCase(fetchOperators.pending, (state) => {
      state.fetching = true;
      state.error = null;
    });
    builder.addCase(fetchOperators.fulfilled, (state, action) => {
      state.operators = action.payload;
      state.fetching = false;
      state.error = null;
    });
    builder.addCase(fetchOperators.rejected, (state, action) => {
      state.fetching = false;
      state.error = action.error.message || 'Failed to fetch operators';
    });
  }
});

export const {
  selectOperator,
  setOperatorInjured,
  clearOperatorInfo,
  setOperators,
  setCurrentUserAsOperator
} = operatorInfoSlice.actions;

export default operatorInfoSlice.reducer;

// Helper functions
export const getOperatorDisplayName = (operator: Operator): string => {
  return `${operator.firstName} ${operator.lastName}`.trim();
};

export const isOperatorInfoComplete = (state: OperatorInfoState): boolean => {
  return state.selectedOperator !== null && state.operatorInjured !== null;
};

export const findOperatorByEmail = (
  operators: Operator[],
  email: string
): Operator | undefined => {
  return operators.find(op => op.email.toLowerCase() === email.toLowerCase());
};